import { Link } from "wouter";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  breadcrumbSchema,
  faqSchema,
  personSchema,
  webPageSchema,
} from "@/lib/seo";

import { withNetworkUtm } from "@/lib/network";
import { NetworkNextStep } from "@/components/NetworkNextStep";
interface QA {
  q: string;
  a: React.ReactNode;
  aText: string;
}

interface Section {
  title: string;
  items: QA[];
}

const SECTIONS: Section[] = [
  {
    title: "About This Site",
    items: [
      {
        q: "Is shariqrefai.com a clinical service?",
        aText:
          "No. This is an educational, author, publishing, media, and founder platform. For clinical care, visit shrinkmd.com, the separate telepsychiatry practice Dr. Refai founded.",
        a: (
          <>
            No. This is an educational, author, publishing, media, and
            founder platform. For clinical care, visit{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              shrinkmd.com
            </a>
            , the separate telepsychiatry practice Dr. Refai founded.
          </>
        ),
      },
      {
        q: "Who writes the content?",
        aText:
          "Every essay, book page, and educational piece is authored and editorially reviewed by Shariq Refai, MD, MBA, board-certified psychiatrist. See the Editorial Process page for more.",
        a: (
          <>
            Every essay, book page, and educational piece is authored and
            editorially reviewed by Shariq Refai, MD, MBA, board-certified
            psychiatrist. See the{" "}
            <Link
              href="/editorial-process"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Editorial Process page
            </Link>{" "}
            for more.
          </>
        ),
      },
      {
        q: "Does the site collect personal information?",
        aText:
          "The site collects only the limited information submitted through the contact form. It doesn't run ads, take affiliate commissions, or sell or share personal information. See the Privacy Policy.",
        a: (
          <>
            The site collects only the limited information submitted through
            the contact form. It doesn't run ads, take affiliate
            commissions, or sell or share personal information. See the{" "}
            <Link
              href="/privacy"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              Privacy Policy
            </Link>
            .
          </>
        ),
      },
    ],
  },
  {
    title: "About Dr. Refai",
    items: [
      {
        q: "What are Dr. Refai's credentials?",
        aText:
          "board-certified in psychiatry through the American Board of Psychiatry and Neurology, and in sports and performance psychiatry through the American Board of Sports and Performance Psychiatry. Fellow of the American Psychiatric Association. MBA from Duke University Fuqua School of Business.",
        a: (
          <>
            board-certified in psychiatry through the American Board of
            Psychiatry and Neurology, and in sports and performance
            psychiatry through the American Board of Sports and Performance
            Psychiatry. Fellow of the American Psychiatric Association. MBA
            from Duke University Fuqua School of Business.
          </>
        ),
      },
      {
        q: "Where is Dr. Refai based?",
        aText: "Jacksonville, Florida.",
        a: <>Jacksonville, Florida.</>,
      },
      {
        q: "Does Dr. Refai accept new patients through this site?",
        aText:
          "No. New patient inquiries should go to shrinkmd.com.",
        a: (
          <>
            No. New patient inquiries should go to{" "}
            <a
              href={withNetworkUtm("https://shrinkmd.com/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              shrinkmd.com
            </a>
            .
          </>
        ),
      },
    ],
  },
  {
    title: "About the Books",
    items: [
      {
        q: "When will the books be released?",
        aText:
          "All three books are scheduled for release in 2026 through shrinkMD Publishing.",
        a: (
          <>
            All three books are scheduled for release in 2026 through
            shrinkMD Publishing.
          </>
        ),
      },
      {
        q: "Will there be audiobooks?",
        aText:
          "Audiobook details will be announced alongside print and ebook release.",
        a: (
          <>Audiobook details will be announced alongside print and ebook release.</>
        ),
      },
      {
        q: "Are the books therapy?",
        aText:
          "No. The books are educational and informational. They aren't therapy, psychiatric treatment, diagnosis, or individualized medical advice.",
        a: (
          <>
            No. The books are educational and informational. They aren't
            therapy, psychiatric treatment, diagnosis, or individualized
            medical advice.
          </>
        ),
      },
    ],
  },
  {
    title: "Working Together",
    items: [
      {
        q: "How do I request a media interview?",
        aText:
          "Use the contact form with \"Media & Press\" selected.",
        a: (
          <>
            Use the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>{" "}
            with "Media &amp; Press" selected.
          </>
        ),
      },
      {
        q: "How do I book Dr. Refai for a speaking engagement?",
        aText:
          "Use the contact form with \"Speaking Engagement\" selected.",
        a: (
          <>
            Use the{" "}
            <Link
              href="/contact"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              contact form
            </Link>{" "}
            with "Speaking Engagement" selected.
          </>
        ),
      },
      {
        q: "Does Dr. Refai do podcast appearances?",
        aText:
          "Yes, for relevant topics. Use the contact form with \"Media & Press\" selected.",
        a: (
          <>
            Yes, for relevant topics. Use the contact form with "Media
            &amp; Press" selected.
          </>
        ),
      },
    ],
  },
  {
    title: "Resources",
    items: [
      {
        q: "Where can I find more reading on anxiety?",
        aText:
          "AnxietyResource.org, the independent editorial publication Dr. Refai serves as medical reviewer for.",
        a: (
          <>
            <a
              href={withNetworkUtm("https://anxietyresource.org/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              AnxietyResource.org
            </a>
            , the independent editorial publication Dr. Refai serves as
            medical reviewer for.
          </>
        ),
      },
      {
        q: "Where can I find more reading on depression?",
        aText:
          "DepressionResource.org, the independent editorial publication Dr. Refai serves as medical reviewer for.",
        a: (
          <>
            <a
              href={withNetworkUtm("https://depressionresource.org/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              DepressionResource.org
            </a>
            , the independent editorial publication Dr. Refai serves as
            medical reviewer for.
          </>
        ),
      },
      {
        q: "Where can I find plain-language summaries of anxiety research?",
        aText:
          "AnxietyResearch.org, the independent editorial publication translating current anxiety research for general readers, which Dr. Refai serves as medical reviewer for.",
        a: (
          <>
            <a
              href={withNetworkUtm("https://anxietyresearch.org/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              AnxietyResearch.org
            </a>
            , the independent editorial publication translating current
            anxiety research into plain-language summaries for general
            readers. Dr. Refai serves as its medical reviewer.
          </>
        ),
      },
      {
        q: "Where can I find plain-language information on a psychiatric medication?",
        aText:
          "PsychiatryRx.org, the independent reference site publishing psychiatrist-reviewed guides to psychiatric and sleep medications. Dr. Refai serves as its medical reviewer. It isn't a pharmacy, a clinic, or a place to obtain prescriptions.",
        a: (
          <>
            <a
              href={withNetworkUtm("https://psychiatryrx.org/", "faq_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
            >
              PsychiatryRx.org
            </a>
            , the independent reference site publishing psychiatrist-reviewed
            guides to psychiatric and sleep medications, sourced from FDA
            labeling and clinical guidelines. Dr. Refai serves as its
            medical reviewer. It isn't a pharmacy, a clinic, or a place to
            obtain prescriptions.
          </>
        ),
      },
      {
        q: "What if I'm in crisis right now?",
        aText:
          "Call or text 988 in the United States, call 911, or go to your nearest emergency room.",
        a: (
          <>
            Call or text 988 in the United States, call 911, or go to your
            nearest emergency room.
          </>
        ),
      },
    ],
  },
];

export default function FAQ() {
  const TITLE = "Frequently Asked Questions | Shariq Refai, MD, MBA";
  const DESCRIPTION =
    "Frequently asked questions about Shariq Refai, MD, MBA, this educational platform, the books, the perspectives essays, shrinkMD, and how to work together.";

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "FAQ", path: "/faq" },
  ];

  const allQAs = SECTIONS.flatMap((s) =>
    s.items.map((i) => ({ question: i.q, answer: i.aText })),
  );

  return (
    <SiteLayout>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        url="/faq"
        schema={[
          personSchema,
          webPageSchema({
            path: "/faq",
            title: TITLE,
            description: DESCRIPTION,
            breadcrumbs: crumbs,
            speakableSelectors: [".faq"],
          }),
          breadcrumbSchema(crumbs),
          faqSchema(allQAs),
        ]}
      />

      <article className="container mx-auto max-w-3xl px-4 md:px-8 py-16 md:py-24">
        <Breadcrumbs
          items={crumbs.map((c) => ({ name: c.name, href: c.path }))}
        />
        <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight mb-6">
          Frequently Asked Questions
        </h1>
        <p className="text-lg text-secondary font-light leading-relaxed mb-12">
          The most common questions from across the site, gathered in one
          place.
        </p>

        {SECTIONS.map((section) => (
          <section key={section.title} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-serif text-primary mb-6 border-b border-border pb-3">
              {section.title}
            </h2>
            <dl className="grid gap-6">
              {section.items.map((qa, i) => (
                <div key={i}>
                  <dt className="font-serif text-lg text-primary mb-2">
                    {qa.q}
                  </dt>
                  <dd className="text-secondary font-light leading-relaxed">
                    {qa.a}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </article>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="faq_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
